// // Re-export all booking interfaces from the existing Bookings folder
// export * from './Bookings';

import { EventLocation } from "./interfaces-data";

export type CreateBookingsProgress =
  | "Meeting Details"
  | "Availability"
  | "Location"
  | "Customize"
  | "Page created";

export type DaysOfTheWeek =
  | "Sunday"
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday";

export interface TimeSchedule {
  start: number; // Changed from string to number
  end: number; // Changed from string to number
  selected: boolean; //remove this when creating bookings
  booked?: boolean;
}

// TimeSchedule for API payload (without 'selected' property)
export interface TimeSchedulePayload {
  start: number;
  end: number;
}

export interface DateEntry {
  day: DaysOfTheWeek;
  timeSchedule: TimeSchedule[];
}

export interface DateAvailability {
  day: string;
  timeSchedule: TimeSchedule[];
}

// DateAvailability for API payload
export interface DateAvailabilityPayload {
  day: string;
  timeSchedule: TimeSchedulePayload[];
}

// export type PreferredApps = 0 | 1 |2|3|4

// BookingsSetUp for local state (with 'selected' property)
export interface BookingsSetUp {
  title: string;
  description: string;
  availability: number;
  dateAvailability: number;
  dates: DateAvailability[]; // Updated to use an array of DateAvailability
  dailyLimits: number;
  currency: string;
  price: number;
  timezone: string;
  preferredApps: number[];
  requestPhone: boolean;
  requestAddress: boolean;
  autoConfirm: boolean | null;
  //   meetingLink: string;
  // providerRefresh: string;
}

// BookingsSetUpPayload for API submission (without 'selected' property)
export interface BookingsSetUpPayload {
  title: string;
  description: string;
  availability: number;
  dateAvailability: number;
  dates: DateAvailabilityPayload[];
  dailyLimits: number;
  currency: string;
  price: number;
  timezone: string;
  preferredApps: number[];
  requestPhone: boolean;
  requestAddress: boolean;
  autoConfirm: boolean | null;
}

export interface CustomDates {
  dates: DateAvailability[];
}

export interface FetchedBookings {
  title: string;
  description: string;
  ownerId: string;
  availability: number;
  dateAvailability: number;
  dates: string; // JSON string containing DateAvailability[]
  dailyLimits: number;
  price: number;
  currency: string;
  timezone: string;
  meetingLink: string;
  requestPhone: boolean;
  requestAddress: boolean;
  deleted: boolean;
  dateAndTime: DateAvailability[];
  dateCreated: string; // ISO 8601 format
  dateModified: string; // ISO 8601 format
  id: string;
  autoConfirm: boolean; // Newly added
  preferredApps: string; // Newly added
}

export interface BookingsPagination {
  totalData: number;
  pageLimit: number;
  currentPage: number;
  currentPageData: number;
  totalPages: number;
  prevPage: number | null;
  nextPage: number | null;
}

export interface BookingsFilter {
  query: string | null;
  startDate: string | null;
  endDate: string | null;
  page: number;
  pageLimit: number;
  range: number;
  sortBy: string | null;
  sortDirection: number;
}

export interface GetBookingsParams {
  query?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageLimit?: number;
  range?: number;
  sortBy?: string;
  sortDirection?: 0 | 1;
  username?: string;
  deleted?: boolean;
}

export interface GetBookingsResponseData {
  pagination: BookingsPagination;
  data: FetchedBookings[];
  filter: BookingsFilter;
}

export interface GetBookingsResponse {
  message: string;
  status: string;
  data: GetBookingsResponseData;
}

// interface BookedSlot {
//   start: number;
//   end: number;
// }

interface Booking {
  start: string; // ISO date string
  end: string; // ISO date string
}

interface BookedDate {
  date: string; // MM/DD/YYYY format
  bookings: Booking[];
}

export interface FetchedCalenderInterface {
  bookedSlots: BookedDate[];
  calendar: FetchedBookings;
}

// export interface UserSettings {
//   color: string;
//   textColor: string;
//   description: string;
//   preferredLayout: number;
//   username: string;
// }

export interface BookedBooking {
  slotId: string;
  email: string;
  name: string;
  description: string;
  phone: string;
  address: string;
  start: string;
  end: string;
  timezone: string;
  paid: boolean;
  status: number; // 0=Not_Paid, 1=Paid, 2=Pending, 3=Confirmed, 4=Cancelled
  amount: number;
  paymentReference: string | null;
  id: string;
  preferedApp: number;
  meetingUrl: string | null;
  deleted: boolean;
  slotTitle: string;
  currency: string;
  reminderCount: number;
  reminderLastSent: string | null;
  reservationExpiry: string | null;
  dateReserved: string | null;
  dateModified: string | null;
}

export interface GetMyBookingsParams {
  query?: string;
  page?: number;
  pageLimit?: number;
  range?: number;
  sortBy?: string;
  sortDirection?: 0 | 1;
  status?: 0 | 1 | 2 | 3 | 4;
  startDate?: string;
  endDate?: string;
}

export interface GetMyBookingsResponseData {
  pagination: BookingsPagination;
  data: BookedBooking[];
}

export interface GetMyBookingsResponse {
  message: string;
  status: string;
  data: GetMyBookingsResponseData;
}

export interface BookedDatenBookings {
  date: string; // "2/2/2025"
  bookings: BookedBooking[];
}

export type Tab = "SelectDate" | "Payment" | "AddToCalendar";
export interface Slot {
  slotId: string;
  start: string;
  end: string;
  email: string;
  name: string;
  description: string;
  phone: string;
  address: string;
  timezone: string;
  preferedApp: number | null;
}

export interface Currency {
  code: string;
  number: string;
  digits: number;
  currency: string;
  countries: string[];
}

export interface ConfirmBookingPayload {
  app: number;
  meetingLink?: string;
}

export interface InviteToSpeakPayload {
  eventId: string;
  sessionId: string;
  speakingFormat: string;
  proposedTopic: string;
  description: string;
  duration: number;
  note: string;
  proposedAmount: number;
  currency: string;
}

// FetchedApp has been moved to settings.interface.ts
// since /Apps is a settings-related endpoint

export enum SpeakingInvitationStatus {
  Pending = "Pending",
  Accepted = "Accepted",
  Declined = "Declined",
}

export type SpeakingInvitationStatusCode = 0 | 1 | 2;
export type SortDirectionCode = 0 | 1;
export type SpeakingInvitationType = "Sent" | "Received";

export interface SpeakingInvitation {
  speakerId: string;
  email: string;
  eventId: string;
  eventTitle: string;
  eventDate: string;
  eventLink: string | null;
  eventLocation: EventLocation | null;
  sessionId: string;
  speakingFormat: string;
  proposedTopic: string;
  description: string;
  duration: number;
  note: string;
  proposedAmount: number;
  currency: string;
  dateSent: string;
  status: SpeakingInvitationStatus;
  type: SpeakingInvitationType;
  dateResponded: string | null;
  id: string;
}

export interface GetSpeakingInvitationsParams {
  page?: number;
  pageLimit?: number;
  query?: string;
  startDate?: string;
  endDate?: string;
  status?: SpeakingInvitationStatusCode;
  sortDirection?: SortDirectionCode;
}

export interface SpeakingInvitationsResponse {
  items: SpeakingInvitation[];
  pagination?: {
    totalData: number;
    pageLimit: number;
    currentPage: number;
    currentPageData: number;
    totalPages: number;
    prevPage: number | null;
    nextPage: number | null;
  };
}

export interface SendCorrespondence {
  message: string;
  id: string;
}

export interface GetCorrespondence {
  invitationId: string;
  message: string;
  sender: string;
  senderName: string;
  senderImage: string;
  date: string;
  id: string;
}

export interface RespondToInvitation {
  id: string;
  status: SpeakingInvitationStatus;
}
